import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import EventLogModal from './EventLogModal';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { APPRoutes } from '../../Utils/Route';
import { useAuth } from '../../Context/AuthContext';
import { saveProviderEventLog, deleteProviderEventLog, getAllProviderEventLogs } from '../../Services/ProviderService';

export default function EventLog({ initialData, providerId, onSaveSuccess, onContinue }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [items, setItems] = useState(Array.isArray(initialData?.eventLogs) ? initialData.eventLogs : []);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });

  const getProviderIdFromStorage = () => {
    try {
      return localStorage.getItem('providerForm.providerId');
    } catch (error) {
      console.error('Failed to read provider id from localStorage:', error);
      return null;
    }
  };
  const getOrganizationIdFromStorage = () => {
    try {
      return localStorage.getItem('providerForm.organizationId');
    } catch (error) {
      console.error('Failed to read organization id from localStorage:', error);
      return null;
    }
  };

  const refreshEventLogs = async () => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    if (!token || !providerIdToUse) return [];

    setLoading(true);
    try {
      const response = await getAllProviderEventLogs(token, providerIdToUse);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setItems(refreshed);
        if (onSaveSuccess) onSaveSuccess(refreshed);
        return refreshed;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        //toast.error(response?.message || 'Failed to load event logs.');
      }
    } catch (error) {
      console.error('Error loading event logs:', error);
    } finally {
      setLoading(false);
    }
    return [];
  };

  useEffect(() => {
    setItems(Array.isArray(initialData?.eventLogs) ? initialData.eventLogs : []);
  }, [initialData]);

  useEffect(() => {
    if (!token || !providerId) return;
    if (Array.isArray(initialData?.eventLogs) && initialData.eventLogs.length > 0) return;
    refreshEventLogs();
  }, [token, providerId, initialData]);

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  const buildPayload = (values) => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    const organizationIdToUse = values.organization_id || getOrganizationIdFromStorage() || null;
    const isUpdate = Boolean(values.id || values.Id || values.event_log_id);

    return {
      ...values,
      provider_id: providerIdToUse,
      organization_id: organizationIdToUse,
      type: isUpdate ? 'update' : 'create',
    };
  };

  const handleSave = async (values) => {
    const payload = buildPayload(values);
    if (!payload.provider_id) {
      toast.error('Provider ID is required to save event log.');
      return;
    }

    setSaving(true);
    try {
      const response = await saveProviderEventLog(token, payload);
      if (response?.is_success) {
        await refreshEventLogs();
        setShowModal(false);
        setEditingIndex(null);
        toast.success('Event log saved successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save event log.');
      }
    } catch (error) {
      console.error('Error saving event log:', error);
      toast.error(error?.message || 'Failed to save event log.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const optimisticUpdate = (updated) => {
      setItems(updated);
      if (onSaveSuccess) onSaveSuccess(updated);
    };

    if (!item || !(item.id || item.Id || item.event_log_id)) {
      const updated = items.filter((_, idx) => idx !== index);
      optimisticUpdate(updated);
      return;
    }

    setDeletingIndex(index);
    try {
      const id = item.id || item.Id || item.event_log_id;
      const response = await deleteProviderEventLog(token, id);
      if (response?.is_success) {
        const refreshed = await refreshEventLogs();
        if (Array.isArray(refreshed)) {
          optimisticUpdate(refreshed);
        } else {
          const updated = items.filter((_, idx) => idx !== index);
          optimisticUpdate(updated);
        }
        toast.success('Event log deleted successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to delete event log.');
      }
    } catch (error) {
      console.error('Error deleting event log:', error);
      toast.error(error?.message || 'Failed to delete event log.');
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleFinishClick = () => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    if (providerIdToUse) {
      navigate(APPRoutes.PROVIDERS_VIEW.replace(':id', providerIdToUse), {
        state: { providerId: providerIdToUse },
      });
      return;
    }
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h6 className="fw-bold hc-text-primary m-0">Event Log</h6>
          </div>
          <button
            type="button"
            className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
            onClick={handleAdd}
          >
            <MdAdd size={18} /> Add
          </button>
        </div>

      
        {loading ?(
          <div className="text-center py-4 text-muted small">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-4 text-muted small">
            No event log entries added yet. Click "Add" to create one.
          </div>
        ) : (

        <div className="row g-3 mb-3">
          {
            items.map((item, index) => (
              <React.Fragment key={index}>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Event Type
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.event_type_name || 'N/A'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Event Date
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.event_date || 'N/A'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Carrier
                  </div>

                  <div className="fw-semibold text-dark">
                  {item.carrier_name || 'N/A'}
                  </div>

                </div>
              </div>
            </div>




            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Actions
                  </div>

                  <div className="d-flex gap-2">

                    <button type="button" className="btn btn-sm btn-outline-primary rounded-3" onClick={() => handleEdit(index)}>
                          <MdEdit size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger rounded-3 d-flex align-items-center justify-content-center"
                          onClick={() => openDeleteModal(index)}
                          disabled={deletingIndex === index}
                        >
                          {deletingIndex === index ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          ) : (
                            <MdDelete size={16} />
                          )}
                        </button>

                  </div>

                </div>
              </div>
            </div> 
              </React.Fragment>

            ))}
            
          </div>

          )}

        <EventLogModal
          isOpen={showModal}
          providerId={providerId}
          onClose={handleClose}
          onSave={handleSave}
          initialData={editingIndex !== null ? items[editingIndex] : null}
          isLoading={saving}
        />
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="event log entry"
        itemName={deleteModal.item ? (deleteModal.item.event_type_name || deleteModal.item.event_type || 'this event log entry') : 'this event log entry'}
        description="This will permanently delete this event log entry. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleFinishClick}
        >
          Finish
        </button>
      </div>
    </>
  );
}
